import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spinner, Container, Row, Col, Form } from "react-bootstrap";
import {
  isValidTZ,
  isValidHetPey,
  validatePhone,
} from "../utils/fieldsValidator";
import api from "../api";
import { useAuth } from "../AuthContext";
import { gbToSize } from "../utils/bytesSize";
import { CheckLg } from "react-bootstrap-icons";

const FieldLabel = ({ children }) => (
  <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "5px" }}>
    {children}
  </div>
);

const ActionButton = ({ onClick, disabled, children, variant = "primary", style = {} }) => {
  const base = {
    width: "100%",
    padding: "11px 20px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: 600,
    border: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.15s",
  };
  const variants = {
    primary: { background: disabled ? "#e5e7eb" : "#6366f1", color: disabled ? "#9ca3af" : "#fff" },
    danger: { background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca" },
    warning: { background: "#fffbeb", color: "#d97706", border: "1px solid #fde68a" },
    success: { background: "#f0fdf4", color: "#16a34a", border: "1px solid #bbf7d0" },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{ ...base, ...variants[variant], ...style }}>
      {children}
    </button>
  );
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();

  const selectedPlanId =
    location.state?.selectedPlanId ||
    new URLSearchParams(location.search).get("plan_id");

  const isDowngrade =
    location.state?.isDowngrade ||
    new URLSearchParams(location.search).get("downgrade") === "true";

  const urlBillingCycle = new URLSearchParams(location.search).get("billing_cycle");

  const [plan, setPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState(urlBillingCycle || "yearly");
  const [loading, setLoading] = useState(false);
  const [errorCheckout, setErrorCheckout] = useState("");
  const [errorPayment, setErrorPayment] = useState("");

  const [phoneError, setPhoneError] = useState("");

  const [showIframe, setShowIframe] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [paymentId, setPaymentId] = useState(null);
  const [savedCard, setSavedCard] = useState(null);
  const savedCardPaymentUrl = useRef(null);
  const pendingPaymentUrl = useRef(null);
  const pendingSubscriptionUrl = useRef(null);
  const userIsYearly = user?.subscription?.billing_cycle === "yearly";
  const [monthToMonthUpgrade, setMonthToMonthUpgrade] = useState(false);
  const [periodEndDate, setPeriodEndDate] = useState(null);
  const [savedPaymentMethod, setSavedPaymentMethod] = useState(null);
  const [useNewCard, setUseNewCard] = useState(false);

  const [paymentStatus, setPaymentStatus] = useState(null);
  const pollRef = useRef(null);

  const PAYMENT_TIMEOUT = 30000;
  const PAYMENT_POLL_TIMEOUT = 60000;
  const paymentTimeoutRef = useRef(null);
  const pollStartTimeRef = useRef(null);

  const startPaymentTimeout = () => {
    clearPaymentTimeout();
    paymentTimeoutRef.current = setTimeout(() => {
      console.error("Payment timeout: no success or error callback");
      setPayLoading(false);
      setShowIframe(false);
      setErrorPayment("An error occurred with the payment. Try again later.");
      window.Allpay = null;
    }, PAYMENT_TIMEOUT);
  };

  const clearPaymentTimeout = () => {
    if (paymentTimeoutRef.current) {
      clearTimeout(paymentTimeoutRef.current);
      paymentTimeoutRef.current = null;
    }
  };

  const [fullName, setFullName] = useState(
    user?.billing_infos?.full_name ||
      user?.firstname + " " + user?.lastname ||
      ""
  );
  const [company_name, setCompanyName] = useState(
    user?.billing_infos?.company_name || ""
  );
  const [email, setEmail] = useState(
    user?.billing_infos?.email || user?.email || ""
  );
  const [phone, setPhone] = useState(
    user?.billing_infos?.phone || user?.phone || ""
  );
  const [client_tehudat, setClientTehudat] = useState(
    user?.billing_infos?.tz_number || ""
  );
  const [het_pey, setHetPey] = useState(user?.billing_infos?.vat_number || "");

  const iframeRef = useRef(null);

  const startPolling = (paymentRefOverride = null) => {
    const refToUse = paymentRefOverride || paymentId;
    if (!refToUse) return;

    if (pollRef.current) {
      console.log("Clearing existing polling interval");
      clearInterval(pollRef.current);
    }

    pollStartTimeRef.current = Date.now();
    console.log("🔄 Starting payment status polling for:", refToUse);

    pollRef.current = setInterval(async () => {
      const elapsedTime = Date.now() - pollStartTimeRef.current;
      if (elapsedTime > PAYMENT_POLL_TIMEOUT) {
        console.log("⏰ Polling timeout reached");
        clearInterval(pollRef.current);
        pollRef.current = null;
        setPayLoading(false);
        setShowIframe(false);
        setErrorPayment(
          "Payment verification timed out. Please check your subscription status in settings."
        );
        return;
      }

      try {
        const res = await api.get(`/payment/status/${refToUse}`);
        const status = res.data.status;
        console.log(`📊 Payment status: ${status}`);

        if (status === "paid") {
          console.log("✅ Payment PAID - stopping polling");
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPayLoading(false);
          setShowIframe(false);

          if (monthToMonthUpgrade) {
            const autoRenewStatus = res.data.auto_renew_status;
            if (autoRenewStatus === "active") {
              setPaymentStatus("success");
            } else if (autoRenewStatus === "pending" || autoRenewStatus === "failed") {
              if (res.data.subscription_url) {
                pendingSubscriptionUrl.current = res.data.subscription_url;
              }
              setPaymentStatus("auto_renew_failed");
            } else {
              setPaymentStatus("success");
            }
            if (refreshUser) await refreshUser();
            return;
          }

          setPaymentStatus("success");
          if (refreshUser) {
            await refreshUser();
          }
        } else if (status === "failed") {
          console.log("❌ Payment FAILED - stopping polling");
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPayLoading(false);
          setShowIframe(false);
          setErrorPayment("Payment failed. Please try again.");
        } else if (status === "pending") {
          console.log("⏳ Payment still pending, continuing to poll...");
        } else {
          console.log(`⚠️ Unknown payment status: ${status} - stopping polling`);
          clearInterval(pollRef.current);
          pollRef.current = null;
          setPayLoading(false);
        }
      } catch (err) {
        console.error("Polling error", err);
      }
    }, 3000);
  };

  useEffect(() => {
    const checkPendingPayment = async () => {
      const urlParams = new URLSearchParams(location.search);
      const urlPaymentRef = urlParams.get("payment_ref");

      if (urlPaymentRef) {
        console.log("✅ Detected payment_ref in URL:", urlPaymentRef);
        setPaymentId(urlPaymentRef);
        setPayLoading("Verifying payment...");

        try {
          const res = await api.get(`/payment/status/${urlPaymentRef}`);
          console.log("Payment status response:", res.data);

          if (res.data.status === "paid") {
            console.log("✅ Payment confirmed as PAID!");
            setPaymentStatus("success");
            setPayLoading(false);
            if (refreshUser) {
              await refreshUser();
            }
          } else if (res.data.status === "failed") {
            console.log("❌ Payment FAILED");
            setErrorPayment("Payment failed. Please try again.");
            setPayLoading(false);
          } else {
            console.log("⏳ Payment still PENDING, starting polling...");
            startPolling(urlPaymentRef);
          }
        } catch (err) {
          console.error("Error checking payment status from URL", err);
          startPolling(urlPaymentRef);
        }
        return;
      }

      const pendingPaymentData = sessionStorage.getItem('pendingPayment');
      console.log("Checking for pending payment in sessionStorage:", pendingPaymentData);

      if (pendingPaymentData) {
        try {
          const { paymentId: savedPaymentId, subscriptionUrl: savedSubscriptionUrl, timestamp } = JSON.parse(pendingPaymentData);

          if (savedSubscriptionUrl) {
            pendingSubscriptionUrl.current = savedSubscriptionUrl;
            setMonthToMonthUpgrade(true);
          }

          if (Date.now() - timestamp < 5 * 60 * 1000) {
            console.log("✅ Resuming payment verification after redirect:", savedPaymentId);
            sessionStorage.removeItem('pendingPayment');
            sessionStorage.removeItem('currentPayment');
            setPaymentId(savedPaymentId);
            setPayLoading("Verifying payment...");

            try {
              const res = await api.get(`/payment/status/${savedPaymentId}`);
              console.log("Payment status response:", res.data);

              if (res.data.status === "paid") {
                console.log("✅ Payment confirmed as PAID!");
                setPaymentStatus("success");
                setPayLoading(false);
                if (refreshUser) {
                  await refreshUser();
                }
              } else if (res.data.status === "failed") {
                console.log("❌ Payment FAILED");
                setErrorPayment("Payment failed. Please try again.");
                setPayLoading(false);
              } else {
                console.log("⏳ Payment still PENDING, starting polling...");
                startPolling(savedPaymentId);
              }
            } catch (err) {
              console.error("Error checking payment status", err);
              startPolling(savedPaymentId);
            }
          } else {
            console.log("⏰ Payment data too old, clearing...");
            sessionStorage.removeItem('pendingPayment');
            sessionStorage.removeItem('currentPayment');
          }
        } catch (err) {
          console.error("Error parsing pending payment", err);
          sessionStorage.removeItem('pendingPayment');
          sessionStorage.removeItem('currentPayment');
        }
      }
    };

    checkPendingPayment();
  }, [location.search]);

  useEffect(() => {
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
      }
      clearPaymentTimeout();
    };
  }, []);

  useEffect(() => {
    if (user === null) {
      navigate("/login", {
        replace: true,
        state: { from: location.pathname },
      });
    }
  }, [user, navigate, location.pathname]);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get("/plans");
        if (res.data.success) {
          const foundPlan = res.data.data.find(
            (p) => p.plan_id.toString() === selectedPlanId?.toString()
          );
          if (foundPlan) {
            setPlan(foundPlan);
          } else {
            setErrorCheckout("Plan not found.");
          }
        } else {
          setErrorCheckout("Failed to fetch plans.");
        }
      } catch (err) {
        setErrorCheckout(err.response?.data?.detail || "Error fetching plans.");
      }
    };

    fetchPlans();
  }, [selectedPlanId]);

  useEffect(() => {
    const fetchPaymentMethod = async () => {
      try {
        const res = await api.get("/payment/payment-method");
        if (res.data.has_saved_card) {
          setSavedPaymentMethod({ mask: res.data.card_mask, brand: res.data.card_brand });
        }
      } catch (err) {
        // Silent — no saved card or not authenticated yet
      }
    };
    fetchPaymentMethod();
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const cycleFromUrl = urlParams.get("billing_cycle");
    if (cycleFromUrl && (cycleFromUrl === "monthly" || cycleFromUrl === "yearly")) {
      setBillingCycle(cycleFromUrl);
      console.log("Updated billing cycle from URL:", cycleFromUrl);
    }
  }, [location.search]);

  useEffect(() => {
    if (isDowngrade && plan && plan.monthly_price === 0 && plan.yearly_price === 0) {
      setBillingCycle("monthly");
    }
  }, [isDowngrade, plan]);

  useEffect(() => {
    if (!showIframe) return;

    const initAllpay = () => {
      if (!iframeRef.current) return;
      if (!window.AllpayPayment) return;

      if (pendingPaymentUrl.current) {
        iframeRef.current.src = pendingPaymentUrl.current;
        pendingPaymentUrl.current = null;
      }

      window.Allpay = new window.AllpayPayment({
        iframeId: "myPaymentIframe",

        onSuccess: () => {
          console.log("🎉 AllPay onSuccess callback triggered!");
          clearPaymentTimeout();

          const currentPayment = sessionStorage.getItem('currentPayment');
          let payRef = null;
          let subscriptionUrl = null;

          if (currentPayment) {
            const paymentData = JSON.parse(currentPayment);
            payRef = paymentData.paymentId;
            subscriptionUrl = paymentData.subscriptionUrl || null;
            sessionStorage.setItem('pendingPayment', JSON.stringify({
              paymentId: payRef,
              subscriptionUrl: subscriptionUrl,
              timestamp: Date.now()
            }));
          } else {
            console.warn("⚠️ No currentPayment found in sessionStorage!");
          }

          setShowIframe(false);
          setPayLoading("Verifying payment...");
          startPolling(payRef);
        },

        onError: (error_n, error_msg) => {
          clearPaymentTimeout();
          setPayLoading(false);
          console.error("Allpay error", { error_n, error_msg });
          const userMessage = error_msg
            ? error_msg === "Card number not provided"
              ? "Invalid card number."
              : error_msg
            : "Payment failed. Please try again.";
          setErrorPayment(userMessage);
        },
      });

      console.log("AllPay initialized", { iframeSrc: iframeRef.current.src });
    };

    if (window.AllpayPayment) {
      initAllpay();
      return;
    }

    let script = document.querySelector('script[src="https://allpay.to/js/allpay-hf.js"]');
    if (!script) {
      script = document.createElement("script");
      script.src = "https://allpay.to/js/allpay-hf.js";
      script.async = true;
      document.body.appendChild(script);
    }

    script.addEventListener('load', initAllpay);
    return () => script.removeEventListener('load', initAllpay);
  }, [showIframe]);

  if (!plan) {
    return (
      <Container className="my-5 text-center">
        {errorCheckout ? (
          <div style={{
            display: "inline-block",
            background: "#fef2f2", color: "#ef4444",
            border: "1px solid #fecaca",
            borderRadius: "10px", padding: "12px 20px",
            fontSize: "13px", fontWeight: 500,
          }}>
            {errorCheckout}
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", color: "#9ca3af", fontSize: "14px" }}>
            <Spinner size="sm" />
            Loading plan...
          </div>
        )}
      </Container>
    );
  }

  const displayAmount =
    billingCycle === "yearly"
      ? plan.yearly_price.toFixed(2)
      : plan.monthly_price.toFixed(2);

  const displayPeriod = billingCycle === "yearly" ? "year" : "month";
  const isFreePlan = plan && plan.monthly_price === 0 && plan.yearly_price === 0;
  const isEffectiveDowngrade = isDowngrade && (isFreePlan || billingCycle === "monthly" || userIsYearly);

  const handleCheckout = async () => {
    setErrorCheckout("");
    setErrorPayment("");

    if (!isFreePlan && (!fullName || !email)) {
      setErrorCheckout("Please fill all billing information.");
      return;
    }

    setLoading(true);

    if (isEffectiveDowngrade) {
      try {
        const res = await api.post("/payment/downgrade_plan", {
          plan_id: plan.plan_id,
          billing_cycle: billingCycle,
          full_name: fullName,
          company_name: company_name,
          email: email,
          phone: phone,
          client_tehudat: client_tehudat,
          vat_number: het_pey,
        });

        if (res.data.success) {
          setPaymentId(res.data.payment_ref);
          setPaymentStatus("success");
          if (refreshUser) {
            refreshUser();
          }
        } else {
          setErrorCheckout("Failed to downgrade plan.");
        }
      } catch (err) {
        setErrorCheckout(err.response?.data?.detail || "Error during downgrade.");
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const res = await api.post("/payment/create-session", {
          plan_id: plan.plan_id,
          billing_cycle: billingCycle,
          full_name: fullName,
          company_name: company_name,
          email: email,
          phone: phone,
          client_tehudat: client_tehudat,
          vat_number: het_pey,
          use_saved_card: !useNewCard,
        });

        if (res.data.month_to_month_upgrade) {
          setMonthToMonthUpgrade(true);
          setPeriodEndDate(res.data.period_end_date);
          setPaymentId(res.data.payment_ref);
          sessionStorage.setItem('currentPayment', JSON.stringify({
            paymentId: res.data.payment_ref,
            timestamp: Date.now()
          }));
          if (res.data.instant_charge) {
            setSavedCard({ mask: res.data.card_mask, brand: res.data.card_brand });
            setPayLoading("Verifying upgrade fee (Step 1/2)...");
            startPolling(res.data.payment_ref);
          } else {
            pendingPaymentUrl.current = res.data.payment_url;
            setShowIframe(true);
          }
        } else if (res.data.instant_charge && res.data.payment_ref) {
          setPaymentId(res.data.payment_ref);
          sessionStorage.setItem('currentPayment', JSON.stringify({
            paymentId: res.data.payment_ref,
            timestamp: Date.now()
          }));
          setSavedCard({ mask: res.data.card_mask, brand: res.data.card_brand });
          setPayLoading("Verifying payment...");
          startPolling(res.data.payment_ref);
        } else if (res.data.payment_url && res.data.payment_ref) {
          setPaymentId(res.data.payment_ref);
          sessionStorage.setItem('currentPayment', JSON.stringify({
            paymentId: res.data.payment_ref,
            timestamp: Date.now()
          }));

          if (res.data.has_saved_card && res.data.card_mask) {
            setSavedCard({ mask: res.data.card_mask, brand: res.data.card_brand });
            savedCardPaymentUrl.current = res.data.payment_url;
          } else {
            pendingPaymentUrl.current = res.data.payment_url;
            setShowIframe(true);
          }
        } else {
          setErrorCheckout("Failed to create checkout session.");
        }
      } catch (err) {
        setErrorCheckout(err.response?.data?.detail || "Error during checkout.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePay = () => {
    setErrorCheckout("");
    setErrorPayment("");

    if (savedCard && savedCardPaymentUrl.current) {
      setPayLoading("Redirecting to payment...");
      window.location.href = savedCardPaymentUrl.current;
      return;
    }

    if (!window.Allpay) {
      console.error("window.Allpay is null — payment system not initialized");
      setErrorPayment("Payment system not ready. Please go back and try again.");
      return;
    }

    if (!iframeRef.current?.src) {
      console.error("Iframe src missing — AllPay has nothing to submit");
      setErrorPayment("Payment form not loaded yet. Please wait and try again.");
      return;
    }

    console.log("✅ Calling window.Allpay.pay()", { src: iframeRef.current.src });
    setPayLoading("processing payment");
    startPaymentTimeout();

    try {
      window.Allpay.pay();
    } catch (err) {
      console.error("Payment initiation error", err);
      clearPaymentTimeout();
      setErrorCheckout("Payment failed to start.");
      setPayLoading(false);
    }
  };

  const handleBackToBilling = () => {
    setShowIframe(false);
    setSavedCard(null);
    savedCardPaymentUrl.current = null;
    pendingPaymentUrl.current = null;
    pendingSubscriptionUrl.current = null;
    setMonthToMonthUpgrade(false);
    setPeriodEndDate(null);
    setPayLoading(false);
    setErrorCheckout("");
    setErrorPayment("");
    setUseNewCard(false);
    window.Allpay = null;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setPhone(value);
    setPhoneError(validatePhone(value));
  };

  const inputStyle = { borderRadius: "8px", fontSize: "13px", border: "1px solid #e5e7eb" };

  const showBackToPlans = !showIframe && !payLoading && !loading && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed";
  const showBackToBilling = (showIframe || savedCard) && !payLoading && !loading && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed";

  return (
    <Container className="my-5">
      <Row className="justify-content-center">
        <Col xs={12} md={6}>
          <div style={{
            background: "#fff",
            borderRadius: "16px",
            border: "1px solid #e5e7eb",
            boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
            padding: "32px",
          }}>

            {/* HEADER */}
            <div style={{ position: "relative", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: "24px" }}>
              {showBackToPlans && (
                <button
                  onClick={() => navigate("/plans")}
                  style={{
                    position: "absolute", left: 0,
                    background: "none", border: "none",
                    fontSize: "12px", fontWeight: 600, color: "#6b7280",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
                    padding: "4px 8px", borderRadius: "6px",
                    transition: "all 0.15s",
                  }}
                >
                  ← Plans
                </button>
              )}
              {showBackToBilling && (
                <button
                  onClick={handleBackToBilling}
                  style={{
                    position: "absolute", left: 0,
                    background: "none", border: "none",
                    fontSize: "12px", fontWeight: 600, color: "#6b7280",
                    cursor: "pointer", display: "flex", alignItems: "center", gap: "4px",
                    padding: "4px 8px", borderRadius: "6px",
                    transition: "all 0.15s",
                  }}
                >
                  ← Billing info
                </button>
              )}

              <div>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "center", marginBottom: "2px" }}>
                  {plan.plan_name} Plan
                </div>
              </div>
            </div>

            {/* PRICE */}
            {!(isDowngrade && isFreePlan) && (
              <div style={{ textAlign: "center", marginBottom: "24px" }}>
                <div style={{ display: "flex", alignItems: "baseline", justifyContent: "center", gap: "4px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "40px", fontWeight: 700, color: "#111827", lineHeight: 1 }}>
                    ${displayAmount}
                  </span>
                  <span style={{ fontSize: "14px", color: "#9ca3af" }}>/ {displayPeriod}</span>
                </div>
                {plan.expiration_in_day && (
                  <div style={{ fontSize: "12px", color: "#9ca3af" }}>
                    Expires in {plan.expiration_in_day} days
                  </div>
                )}
              </div>
            )}

            {/* BILLING CYCLE TOGGLE */}
            {!(isDowngrade && isFreePlan) && (
              <div style={{ display: "flex", justifyContent: "center", marginBottom: "24px" }}>
                <div style={{ display: "inline-flex", background: "#f1f3f5", borderRadius: "8px", padding: "3px", gap: "2px" }}>
                  {["yearly", "monthly"].map((cycle) => {
                    const active = billingCycle === cycle;
                    const disabled = showIframe || (userIsYearly && cycle === "monthly");
                    return (
                      <button
                        key={cycle}
                        onClick={() => !disabled && setBillingCycle(cycle)}
                        disabled={disabled}
                        style={{
                          padding: "5px 18px", borderRadius: "6px", border: "none",
                          fontSize: "13px", fontWeight: active ? 600 : 400,
                          color: active ? "#111827" : "#6c757d",
                          background: active ? "#ffffff" : "transparent",
                          boxShadow: active ? "0 1px 3px rgba(0,0,0,0.12)" : "none",
                          cursor: disabled ? "not-allowed" : "pointer",
                          transition: "all 0.15s ease", userSelect: "none",
                          opacity: disabled ? 0.5 : 1,
                        }}
                      >
                        {cycle === "monthly" ? "Monthly" : "Yearly"}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PLAN FEATURES */}
            {!(isDowngrade && isFreePlan) && (
              <>
                <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: "16px", marginBottom: "16px" }}>
                  <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "12px" }}>
                    Plan features
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CheckLg size={10} color="#10b981" />
                        </div>
                        <span style={{ fontSize: "13px", color: "#374151" }}>Storage</span>
                      </div>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>{gbToSize(plan.memory_limit_gb)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#ecfdf5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CheckLg size={10} color="#10b981" />
                        </div>
                        <span style={{ fontSize: "13px", color: "#374151" }}>Max Events</span>
                      </div>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: "#111827" }}>
                        {plan.max_events === 0 ? "Unlimited" : plan.max_events}
                      </span>
                    </div>
                    {plan.is_popular > 0 && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: 18, height: 18, borderRadius: "50%", background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <CheckLg size={10} color="#6366f1" />
                        </div>
                        <span style={{ fontSize: "13px", color: "#374151" }}>Most Popular</span>
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ borderTop: "1px solid #f3f4f6", marginBottom: "20px" }} />
              </>
            )}

            {/* FREE PLAN DOWNGRADE */}
            {isDowngrade && isFreePlan && !loading && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed" && (
              <>
                {errorCheckout && (
                  <div style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", marginBottom: "12px" }}>
                    {errorCheckout}
                  </div>
                )}
                <ActionButton onClick={handleCheckout} disabled={loading} variant="danger">
                  {loading ? <><Spinner size="sm" /> Processing...</> : "Downgrade to Free Plan"}
                </ActionButton>
              </>
            )}

            {/* BILLING INFORMATION */}
            {!isFreePlan && !showIframe && !savedCard && !payLoading && !loading && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed" && (
              <>
                <div style={{ fontSize: "10px", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "16px" }}>
                  Billing Information
                </div>

                <Form>
                  <div style={{ marginBottom: "14px" }}>
                    <FieldLabel>Full Name *</FieldLabel>
                    <Form.Control
                      type="text"
                      placeholder="John Doe"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: "14px" }}>
                    <FieldLabel>Email *</FieldLabel>
                    <Form.Control
                      type="email"
                      placeholder="email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: "14px" }}>
                    <FieldLabel>Phone</FieldLabel>
                    <Form.Control
                      type="tel"
                      value={phone}
                      onChange={handlePhoneChange}
                      isInvalid={!!phoneError}
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid">{phoneError}</Form.Control.Feedback>
                  </div>

                  <div style={{ marginBottom: "14px" }}>
                    <FieldLabel>Company Name</FieldLabel>
                    <Form.Control
                      type="text"
                      value={company_name}
                      onChange={(e) => setCompanyName(e.target.value)}
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: "14px" }}>
                    <FieldLabel>Tehudat Zehut</FieldLabel>
                    <Form.Control
                      type="text"
                      value={client_tehudat}
                      onChange={(e) => setClientTehudat(e.target.value)}
                      isInvalid={client_tehudat && !isValidTZ(client_tehudat)}
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid">Invalid Tehudat Zehut</Form.Control.Feedback>
                  </div>

                  <div style={{ marginBottom: "20px" }}>
                    <FieldLabel>Het Pey</FieldLabel>
                    <Form.Control
                      type="text"
                      value={het_pey}
                      onChange={(e) => setHetPey(e.target.value)}
                      isInvalid={het_pey && !isValidHetPey(het_pey)}
                      style={inputStyle}
                    />
                    <Form.Control.Feedback type="invalid">Invalid Het Pey</Form.Control.Feedback>
                  </div>
                </Form>

                {/* Saved payment method */}
                {savedPaymentMethod && !isEffectiveDowngrade && (
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 14px", borderRadius: "10px",
                    border: "1px solid #e5e7eb", background: "#f9fafb",
                    marginBottom: "16px",
                  }}>
                    {useNewCard ? (
                      <>
                        <span style={{ fontSize: "13px", color: "#9ca3af" }}>New card will be entered at the next step</span>
                        <button
                          onClick={() => setUseNewCard(false)}
                          style={{ background: "none", border: "none", fontSize: "12px", fontWeight: 600, color: "#6366f1", cursor: "pointer" }}
                        >
                          Use saved card
                        </button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize: "13px", color: "#374151" }}>
                          Pay with <strong>{savedPaymentMethod.brand}</strong> ending in <strong>{savedPaymentMethod.mask?.slice(-4)}</strong>
                        </span>
                        <button
                          onClick={() => setUseNewCard(true)}
                          style={{ background: "none", border: "none", fontSize: "12px", fontWeight: 600, color: "#6366f1", cursor: "pointer" }}
                        >
                          Use a different card
                        </button>
                      </>
                    )}
                  </div>
                )}

                {errorCheckout && (
                  <div style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", marginBottom: "12px" }}>
                    {errorCheckout}
                  </div>
                )}

                <ActionButton
                  onClick={handleCheckout}
                  disabled={
                    loading ||
                    phoneError !== "" ||
                    (client_tehudat && !isValidTZ(client_tehudat)) ||
                    (het_pey && !isValidHetPey(het_pey))
                  }
                  variant={isEffectiveDowngrade ? "danger" : "primary"}
                >
                  {loading ? (
                    <><Spinner size="sm" /> Processing...</>
                  ) : (
                    isEffectiveDowngrade ? "Downgrade Plan" : "Proceed to Payment"
                  )}
                </ActionButton>
              </>
            )}

            {/* IFRAME */}
            <div style={{
              height: showIframe ? "200px" : "0px",
              transition: "height 0.5s ease",
              overflow: "hidden",
              border: showIframe ? "1px solid #e5e7eb" : "none",
              borderRadius: "10px",
              marginBottom: showIframe ? "16px" : "0",
              padding: showIframe ? "10px" : "0",
            }}>
              <iframe
                id="myPaymentIframe"
                ref={iframeRef}
                title="allpay"
                style={{ width: "100%", height: showIframe ? "200px" : "0px", border: "none" }}
              />
            </div>

            {/* LOADING / POLLING */}
            {(payLoading || loading) && !showIframe && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed" && (
              <div style={{ textAlign: "center", padding: "24px 0" }}>
                <Spinner style={{ color: "#6366f1" }} />
                <div style={{ fontSize: "13px", color: "#9ca3af", marginTop: "12px" }}>{payLoading || "Processing..."}</div>
                {monthToMonthUpgrade && periodEndDate && (
                  <div style={{
                    background: "#eef2ff", border: "1px solid #c7d2fe",
                    borderRadius: "10px", padding: "12px 14px",
                    fontSize: "12px", color: "#4f46e5", textAlign: "left", marginTop: "16px",
                    lineHeight: 1.6,
                  }}>
                    <strong>2-step upgrade:</strong><br />
                    Step 1 — Pay prorata upgrade fee<br />
                    Step 2 — Set up new monthly subscription (starting {new Date(periodEndDate).toLocaleDateString()})
                  </div>
                )}
              </div>
            )}

            {/* ERROR MESSAGES */}
            {errorPayment && (
              <div style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", marginBottom: "12px" }}>
                {errorPayment}
              </div>
            )}
            {errorCheckout && (showIframe || savedCard) && (
              <div style={{ background: "#fef2f2", color: "#ef4444", border: "1px solid #fecaca", borderRadius: "10px", padding: "10px 14px", fontSize: "13px", marginBottom: "12px" }}>
                {errorCheckout}
              </div>
            )}

            {/* SAVED CARD INFO */}
            {savedCard && !showIframe && !payLoading && paymentStatus !== "success" && paymentStatus !== "auto_renew_failed" && (
              <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "10px 14px", borderRadius: "10px",
                background: "#eef2ff", border: "1px solid #c7d2fe",
                fontSize: "13px", color: "#4f46e5", marginBottom: "12px",
              }}>
                Paying with <strong>{savedCard.brand}</strong> card ending in <strong>{savedCard.mask}</strong>
              </div>
            )}

            {/* PAY BUTTON */}
            {(showIframe || (savedCard && !paymentStatus && !payLoading)) && (
              <ActionButton onClick={handlePay} disabled={!!payLoading} variant="primary">
                {payLoading ? (
                  <><Spinner size="sm" /> {payLoading}...</>
                ) : (
                  "Pay Now"
                )}
              </ActionButton>
            )}

            {/* SUCCESS */}
            {paymentStatus === "success" && (
              <div style={{ textAlign: "center" }}>
                <div style={{
                  width: 52, height: 52, borderRadius: "50%",
                  background: "#ecfdf5",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 16px",
                }}>
                  <CheckLg size={24} color="#10b981" />
                </div>
                <div style={{ fontSize: "16px", fontWeight: 700, color: "#111827", marginBottom: "6px" }}>Payment Successful!</div>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "4px" }}>
                  Your subscription has been updated to the <strong>{plan?.plan_name}</strong> plan.
                </div>
                <div style={{ fontSize: "13px", color: "#6b7280", marginBottom: "24px" }}>
                  Billing cycle: <strong>{billingCycle}</strong>
                </div>
                <ActionButton onClick={() => navigate("/admin/settings?tab=billing")} variant="success">
                  View Subscription Details
                </ActionButton>
              </div>
            )}

            {/* AUTO_RENEW_FAILED */}
            {paymentStatus === "auto_renew_failed" && (
              <div style={{ textAlign: "center" }}>
                <div style={{
                  background: "#fffbeb", border: "1px solid #fde68a",
                  borderRadius: "10px", padding: "16px",
                  fontSize: "13px", color: "#92400e", marginBottom: "16px", textAlign: "left",
                }}>
                  <div style={{ fontWeight: 700, marginBottom: "6px" }}>Plan Upgraded!</div>
                  <div>Your subscription has been upgraded to the <strong>{plan?.plan_name}</strong> plan.</div>
                  <div style={{ marginTop: "6px" }}>
                    Auto-renewal could not be set up automatically.
                    {pendingSubscriptionUrl.current && (
                      <> <a href={pendingSubscriptionUrl.current} target="_blank" rel="noreferrer" style={{ color: "#d97706", fontWeight: 600 }}>
                        Click here to set up auto-renewal manually.
                      </a></>
                    )}
                  </div>
                </div>
                <ActionButton onClick={() => navigate("/admin/settings?tab=billing")} variant="warning">
                  View Subscription Details
                </ActionButton>
              </div>
            )}

          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Checkout;
